import { addLogLine } from "./log";
import { SupplierCode } from "./suppliers/supplierCodes";
import { VERIFICATION_SERVER_URL, INIT_SESSION_ROUTE } from "../shared/consts";
import { makeGetRequestHandleResponse } from "./request";
import Mdn from '../shared/mdn';
import { encryptParams } from '../shared/paramsEncoding'

type VerificationParams = {
    sessionId: string,
    supplierType: number,
}
type ShouldVerifyResponse = {
    sv: boolean,
    verificationParams?: VerificationParams,
}

// As this handles a response from our server, we can expect the response to be valid, so throwing an error if it's not
function parseShouldVerifyResponse(responseObj: Object) : ShouldVerifyResponse {
    if (!('sv' in responseObj) || responseObj['sv'] === undefined || responseObj['sv'] === null) {
        addLogLine('sv undefined - Unexpected behavior!');
        throw new Error('sv undefined - Unexpected behavior!');
    }
    if (responseObj['sv'] === false) {
        addLogLine('Got sv false');
        return {
            sv: false,
        };
    }
    // sv is true
    if (!('sessionId' in responseObj) || !responseObj['sessionId']) {
        const errMsg = 'sv true and still sessionId undefined - Unexpected behavior!';
        addLogLine(errMsg);
        throw new Error(errMsg);
    }
    const sessionId = responseObj['sessionId'] as string;
    // sessionId exists
    if (!('supplierType' in responseObj) || !responseObj['supplierType']) {
        const errMsg = 'sv true and still supplierType undefined - Unexpected behavior!';
        addLogLine(errMsg);
        throw new Error(errMsg);
    }
    const supplierType = responseObj['supplierType'] as number;
    if (!(supplierType in SupplierCode)) {
        const errMsg = `Received supplierType ${supplierType} not in SupplierType keys - Unexpected behavior!`;
        addLogLine(errMsg);
        throw new Error(errMsg);
    }
    addLogLine('sv true and got valid sessionId and supplierType - returning valid object');
    return {
        sv: true,
        verificationParams: {
            sessionId,
            supplierType,
        },
    };
}

export async function initPotentialSession(userId: string, siteId: string, extVersion: string, consentId: string, consentTimestamp: string, mdnObj: Mdn, priceString: string) : Promise<ShouldVerifyResponse> {
    addLogLine('initPotentialSession with userId: ' + userId + ', siteId: ' + siteId + ', extVersion: ' + extVersion + ', consentId: ' + consentId + ', consentTimestamp: ' + consentTimestamp + ', mdn: ' + mdnObj + ', priceString: ' + priceString);
    
    const encryptedParamsStr = encryptParams({userId, siteId, extVersion, consentId, consentTimestamp, mdn: mdnObj.getFullFormattedMdn(), priceString});
    if (!encryptedParamsStr) {
        const errMsg = 'Failed to encrypt params';
        addLogLine(errMsg);
        throw new Error(errMsg);
    }
    const encryptedParamQuery = new URLSearchParams({
        p: encryptedParamsStr,
        fvf: 'true'
    });
    const getURL = VERIFICATION_SERVER_URL + INIT_SESSION_ROUTE + '?' + encryptedParamQuery.toString();
    const fetchParsedResponse = await makeGetRequestHandleResponse(getURL);
    if (!fetchParsedResponse.success) {
        // should not happen
        const errMsg = 'Failed to get response from server - response error details -  ' + fetchParsedResponse.errorResponseStr;
        addLogLine(errMsg);
        throw new Error(errMsg);
    }
    // else
    const responseJson = fetchParsedResponse.successResponseObj;
    addLogLine('response: ' + JSON.stringify(responseJson));
    const parsedShouldVerifyResponse = parseShouldVerifyResponse(responseJson);
    return parsedShouldVerifyResponse;
}